import React, { useState, useEffect } from "react";
import { FaHistory, FaUserMd, FaBriefcaseMedical, FaNotesMedical, FaFileInvoiceDollar } from "react-icons/fa";
import { fetchData } from "../../../helpers/fetchData";
import Spinner from "../../../shared/Spinner";
import CustomTable from "../../../shared/CustomTable";
import CustomChip from "../../../shared/CustomChip";
import moment from "moment";

const TrackingContent = ({ trackType, trackId, t }) => {
  const [loading, setLoading] = useState(true);
  const [journeyData, setJourneyData] = useState([]);

  useEffect(() => {
    const fetchTrackingData = async () => {
      if (!trackType || !trackId) return;
      setLoading(true);
      try {
        const endpoint =
          trackType === "visit"
            ? `/v3/patientHistory/visit/${trackId}`
            : `/v3/patientHistory/appointment/${trackId}`;
        const response = await fetchData("POST", endpoint, {});
        
        if (response) {
          let allEvents = [];
          
          if (Array.isArray(response?.history)) {
            allEvents = response.history;
          } else if (response?.history?.appointment_id || response?.history?.visit_id) {
            const appointments = response?.history?.appointment_id || {};
            const visits = response?.history?.visit_id || {};
            
            Object.values(appointments).forEach(events => {
              allEvents.push(...(events || []));
            });
            
            Object.values(visits).forEach(events => {
              allEvents.push(...(events || []));
            });
          } else {
            const history = response?.history || {};
            Object.values(history).forEach(dateData => {
              if (dateData?.visits) {
                Object.values(dateData.visits).forEach(visitEvents => {
                  allEvents.push(...(visitEvents || []));
                });
              }
              if (dateData?.appointments) {
                Object.values(dateData.appointments).forEach(apptEvents => {
                  allEvents.push(...(apptEvents || []));
                });
              }
            });
          }
          
          const sortedEvents = [...allEvents].sort((a, b) => {
            const timeA = new Date(a.created_at).getTime();
            const timeB = new Date(b.created_at).getTime();
            const timeDiff = timeA - timeB;

            if (Math.abs(timeDiff) <= 1000) {
              const priority = {
                'Booked': 1,
                'Arrived': 5,
                'Created': 10,
                'Visit Created': 11,
                'Nurse Assigned': 15,
                'EmrCreated': 18,
                'ready': 19,
                'EmrUpdated': 20,
                'Bill Paid': 25,
                'Fulfilled': 30,
                'Completed': 35
              };
              const pA = priority[a.status] || 99;
              const pB = priority[b.status] || 99;
              return pA - pB;
            }
            return timeDiff;
          });
          
          const getTimeDiffString = (start, end) => {
            if (!start || !end) return '--';
            const diff = moment(end).diff(moment(start));
            if (diff <= 0) return '0s';

            const dur = moment.duration(diff);
            const h = Math.floor(dur.asHours());
            const m = dur.minutes();
            if (h > 0) return `${h}h ${m}m`;
            if (m > 0) return `${m}m`;
            return `${Math.round(dur.asSeconds())}s`;
          };

          const timelineData = sortedEvents.map((event, index) => {
            const firstEvent = sortedEvents[0];
            return {
              ...event,
              no: index + 1,
              waitingTime: index > 0 ? getTimeDiffString(firstEvent.created_at, event.created_at) : '--',
              serviceTime: '--',
            };
          });
          
          // Reverse the array for display (newest first)
          const reversedData = timelineData.reverse();
          
          // Recalculate waiting time after reversal - now from the LAST item (which was first chronologically)
          const recalculatedData = reversedData.map((event, index) => {
            const firstChronologicalEvent = reversedData[reversedData.length - 1]; // Last item in reversed array
            const isFirstEvent = index === reversedData.length - 1;
            return {
              ...event,
              waitingTime: !isFirstEvent ? getTimeDiffString(firstChronologicalEvent.created_at, event.created_at) : '--',
            };
          });
          
          setJourneyData(recalculatedData);
        }
      } catch (error) {
        console.error("Error fetching tracking data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrackingData();
  }, [trackType, trackId]);

  const getIconForStatus = (status) => {
    const s = status?.toLowerCase();
    const iconSize = 12;
    if (s?.includes('emr') || s?.includes('consult')) return <FaUserMd className="text-primary/70" size={iconSize} />;
    if (s?.includes('visit')) return <FaBriefcaseMedical className="text-secondary/70" size={iconSize} />;
    if (s?.includes('vitals') || s?.includes('ready')) return <FaNotesMedical className="text-success/70" size={iconSize} />;
    if (s?.includes('paid') || s?.includes('bill')) return <FaFileInvoiceDollar className="text-teal-600/70" size={iconSize} />;
    return <FaHistory className="text-gray-400" size={iconSize} />;
  };

  const getStatusChip = (status) => {
    const s = status?.toLowerCase();
    let chipType = "mono-outline";
    
    if (['emrcreated', 'ready', 'fulfilled', 'paid', 'booked', 'completed', 'bill paid'].some(v => s?.includes(v))) {
      chipType = "success";
    } else if (s?.includes('in progress') || s?.includes('created')) {
      chipType = "primary";
    } else if (['pending', 'waiting'].some(v => s?.includes(v))) {
      chipType = "warning";
    } else if (s?.includes('updated')) {
      chipType = "blue";
    }

    return (
      <CustomChip
        label={status}
        chipType={chipType}
        className="!h-auto !py-1"
      />
    );
  };

  const timelineColumns = [
    {
      header: t('Phase'),
      body: (rowData, { rowIndex }) => (
        <div className="flex items-center justify-start relative min-h-[50px] pl-2">
          {rowIndex !== journeyData.length - 1 && (
            <div className="absolute top-[30px] bottom-[-26px] left-[18px] w-[2px] bg-gradient-to-b from-primary/30 to-gray-200 z-0"></div>
          )}
          <div className="relative z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white border-2 border-primary/20 shadow-sm">
            {getIconForStatus(rowData.status)}
          </div>
        </div>
      ),
      style: { width: '60px', textAlign: 'left', padding: '0' },
      bodyCellClass: "!border-b-0 !px-0"
    },
    {
      header: t('Activity / Action'),
      body: (rowData) => (
       rowData.action_reason || rowData.status
      ),
    },
    {
      header: t('Performed By'),
      body: (rowData) => (
          rowData.action_performed_by?.name  +" "+ `(${rowData.action_performed_by?.role})`
      ),
    },
    {
      header: t('Time'),
      body: (rowData) => (
     moment(rowData.created_at).format('DD MMM YYYY hh:mm A')
      ),
    },
    {
      header: (
        <div className="flex items-center gap-1">
          <span>{t('Wait Time')}</span>
        </div>
      ),
      body: (rowData) => (
          rowData.waitingTime !== '--' ? (
    rowData.waitingTime
          ) : (
            <span className="text-[12px] text-gray-400 font-medium">--</span>
          )
      ),
    },
    {
      header: t('Status'),
      body: (rowData) => rowData.status,
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <Spinner fill="var(--primary)" width="28px" height="28px" containerClass="" />
      </div>
    );
  }

  if (journeyData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <span className="text-12-gray">{t("No tracking data found")}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <CustomTable
        data={journeyData}
        columns={timelineColumns}
        emptyMessage={t('No records found')}
      />
    </div>
  );
};

export default TrackingContent;
